<template>
    <section class="section py-5" id="contact">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 text-center">
                    <div class="section-title mb-5">
                        <h2 class="title mt-5 fw-bold">Get In Touch</h2>
                        <p class="text-muted para-desc mx-auto mb-0" style="max-width: 600px">
                            Have a question or need assistance? We're here to help. Fill out the form below and we'll get back to you as soon as possible.
                        </p>
                    </div>
                </div>
            </div>

            <div class="row justify-content-center">
                <div class="col-lg-8 col-md-10">
                    <div class="card shadow-sm border-0">
                        <div class="card-body p-4 p-md-5">
                            <div class="text-center mb-4">
                                <h3 class="card-title fw-semibold mb-3">Contact Us</h3>
                                <div class="mx-auto" style="width: 80px; height: 3px; background: #6c63ff;"></div>
                            </div>
                            
                            <div class="custom-form">
                                <form @submit.prevent="submitForm">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label class="form-label">Your Name <span class="text-danger">*</span></label>
                                                <div class="form-icon position-relative">
                                                    <i data-feather="user" class="fea icon-sm icons"></i>
                                                    <input v-model="formData.name" name="name" type="text" class="form-control ps-5" placeholder="Name" required>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label class="form-label">Your Email <span class="text-danger">*</span></label>
                                                <div class="form-icon position-relative">
                                                    <i data-feather="mail" class="fea icon-sm icons"></i>
                                                    <input v-model="formData.email" name="email" type="email" class="form-control ps-5" placeholder="Email" required>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-12">
                                            <div class="mb-3">
                                                <label class="form-label">Subject</label>
                                                <div class="form-icon position-relative">
                                                    <i data-feather="book" class="fea icon-sm icons"></i>
                                                    <input v-model="formData.subject" name="subject" class="form-control ps-5" placeholder="Subject">
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-12">
                                            <div class="mb-3">
                                                <label class="form-label">Message <span class="text-danger">*</span></label>
                                                <div class="form-icon position-relative">
                                                    <i data-feather="message-circle" class="fea icon-sm icons" style="top: 18px; transform: none;"></i>
                                                    <textarea v-model="formData.message" name="message" rows="4" class="form-control ps-5" placeholder="Message" required></textarea>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Math verification - only shown after initial form submission -->
                                        <div class="col-12" v-if="showMathVerification">
                                            <div class="mb-3">
                                                <label class="form-label">Verification: What is {{ mathQuestion }}? <span class="text-danger">*</span></label>
                                                <div class="form-icon position-relative">
                                                    <i data-feather="shield" class="fea icon-sm icons"></i>
                                                    <input v-model="userAnswer" type="number" class="form-control ps-5" placeholder="Your answer" required>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-12">
                                            <div class="d-grid">
                                                <button type="submit" :disabled="isSubmitting" class="btn btn-primary d-flex align-items-center justify-content-center">
                                                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    <span v-if="!showMathVerification">Continue</span>
                                                    <span v-else>Send Message</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                                
                                <div v-if="formSuccess" class="alert alert-success mt-4">
                                    Thank you! Your message has been sent successfully.
                                </div>
                                <div v-if="formError" class="alert alert-danger mt-4">
                                    {{ formError }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.section {
    min-height: 100vh;
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
}

.card {
    border-radius: 12px;
    border: none;
    background: white;
}

.form-control {
    border-radius: 8px;
    padding: 12px 16px 12px 48px;
    border: 1px solid #dee2e6;
}

.form-control:focus {
    border-color: #6c63ff;
    box-shadow: 0 0 0 0.2rem rgba(108, 99, 255, 0.15);
}

.btn-primary {
    background-color: #6c63ff;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 500;
}

.btn-primary:hover {
    background-color: #5a52e0;
}

/* Keep existing icon positioning */
.form-icon .icons {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 16px;
    color: #6c63ff;
}
</style>

<script>
import feather from 'feather-icons';
import axios from 'axios';

export default {
    name: "GetInTouch",
    data() {
        // Generate two random numbers between 1-10
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        
        return {
            formData: {
                name: '',
                email: '',
                subject: '',
                message: ''
            },
            isSubmitting: false,
            formSuccess: false,
            formLoadTime: new Date().getTime(),
            formError: null,
            showMathVerification: false, // Initially hide math verification
            mathQuestion: `${num1} + ${num2}`,
            mathAnswer: num1 + num2,
            userAnswer: null,
        }
    },
    mounted() {
        feather.replace();
    },
    methods: {
        
        async submitForm() {
            // If math verification is not shown yet, show it and return
            if (!this.showMathVerification) {
                // Validate required fields first
                if (!this.formData.name || !this.formData.email || !this.formData.message) {
                    this.formError = 'Please fill in all required fields.';
                    return;
                }
                
                // Show math verification and regenerate question
                this.showMathVerification = true;
                this.regenerateMathQuestion();
                this.formError = null;
                return;
            }
            
            // If we're here, math verification is shown and user is submitting the answer
            
            // Validate math answer
            if (parseInt(this.userAnswer) !== this.mathAnswer) {
                this.formError = 'Please provide the correct answer to the math question.';
                return;
            }
            
            const currentTime = new Date().getTime();
            const timeDiff = currentTime - this.formLoadTime;
        
            // If form submitted in less than 3 seconds, likely a bot
            if (timeDiff < 3000) {
                console.log('Submission too quick, likely a bot');
                this.simulateSuccess();
                return;
            }
            
            this.isSubmitting = true;
            this.formSuccess = false;
            this.formError = null;
            
            try {
                // Send the form data to the backend
                const response = await axios.post(
                    `${process.env.VUE_APP_BASE_URL}/contact/send`, 
                    this.formData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'api-key': process.env.VUE_APP_API_KEY
                        }
                    }
                );
                
                if (response.status !== 201) {
                    throw new Error('Failed to send message. Please try again later.');
                }
                
                // Reset form after successful submission
                this.formData = {
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                };
                this.formSuccess = true;
                this.showMathVerification = false;
                this.userAnswer = null;
                
            } catch (error) {
                console.error('Form submission error:', error);
                this.formError = error.message || 'An error occurred while sending your message. Please try again.';
            } finally {
                this.isSubmitting = false;
            }
        },
        
        regenerateMathQuestion() {
            const num1 = Math.floor(Math.random() * 10) + 1;
            const num2 = Math.floor(Math.random() * 10) + 1;
            this.mathQuestion = `${num1} + ${num2}`;
            this.mathAnswer = num1 + num2;
            this.userAnswer = null;
        },
        
        simulateSuccess() {
            this.isSubmitting = true;
            setTimeout(() => {
                this.formSuccess = true;
                this.isSubmitting = false;
                this.showMathVerification = false;
                // Reset form
                this.formData = {
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                };
            }, 1500);
        }
    }
}</script>
